export const ADVISOR_SUPPORTED_MODES = ['practice', 'private', 'public', 'community', 'daily10'] as const
export type AdvisorSupportedModeValue = (typeof ADVISOR_SUPPORTED_MODES)[number]

// Default mode allowlist for the advisor (used by the app's remote-config
// schema default; an explicit Firebase value can still override).
//
// PRODUCT DECISION: practice only. Practice is the solo (vs bots) game, and
// the advisor is for solo play only: private games no longer get it. This
// single allowlist is the advisor's mode gate; the other modes re-enable
// later purely via remote config, no code change. Exclusions:
// - private: advisor removed from private games; practice/solo only.
// - public / community / mtt: hidden until re-enabled.
// - daily10: its limited free preflop folds (and its out-of-budget timeout
//   auto-CALL) make budget-aware strategy deliberately different from
//   standard poker — the budget-blind expert policy would either stay silent
//   in exactly the interesting spots or recommend folds the game forces
//   players not to make. TODO: budget-aware daily10 coaching — thread
//   freePreflopFoldsUsed/Allowed into the expert policy (fold only while
//   budget remains; play out weak hands once spent) and coach the budget
//   decision explicitly.
// - public / community / mtt: hidden until re-enabled.
export const DEFAULT_ADVISOR_SUPPORTED_MODES: readonly AdvisorSupportedModeValue[] = ['practice'] as const

export const BOT_DECISION_MODE = {
  live: 'live',
  advisor: 'advisor'
} as const
export type BotDecisionModeValue = (typeof BOT_DECISION_MODE)[keyof typeof BOT_DECISION_MODE]

export const BOT_ACTION_CONDITION_TYPE = {
  aggressorRaise: 'AggressorRaise',
  betInBoard: 'BetInBoard',
  curiosityCall: 'CuriosityCall',
  defaultPlayerAction: 'DefaultPlayerAction',
  draws: 'Draws',
  excellentHand: 'ExcellentHand',
  faceCardDefense: 'FaceCardDefense',
  faceCardSpeculativeCall: 'FaceCardSpeculativeCall',
  fallbackStreetState: 'FallbackStreetState',
  flopCheckRaise: 'FlopCheckRaise',
  insufficientChips: 'InsufficientChips',
  isPreFlopAggressor: 'IsPreFlopAggressor',
  looseCall: 'LooseCall',
  moreThan3Players: 'MoreThan3Players',
  noAvailableActions: 'NoAvailableActions',
  noBetInBoard: 'NoBetInBoard',
  noBets: 'NoBets',
  noGoodHand: 'NoGoodHand',
  noMatch: 'NoMatch',
  noOtherBet: 'NoOtherBet',
  noStrongHand: 'NoStrongHand',
  nothingToDo: 'NothingToDo',
  pair: 'Pair',
  pairOf: 'PairOf',
  potOddsFail: 'PotOddsFail',
  riverBluff: 'RiverBluff',
  riverValueBet: 'RiverValueBet',
  semiBluffRaise: 'SemiBluffRaise',
  strongHand: 'StrongHand',
  strongMadeHand: 'StrongMadeHand',
  suitedConnectors: 'SuitedConnectors',
  tableMinimumRaise: 'TableMinimumRaise',
  turnGiveUp: 'TurnGiveUp',
  twoHigh: 'TwoHigh',
  twoPair: 'TwoPair',
  unlucky: 'Unlucky',
  valueOf: 'ValueOf',
  bigBlindTurn: 'BigBlindTurn'
} as const
export type BotActionConditionType = (typeof BOT_ACTION_CONDITION_TYPE)[keyof typeof BOT_ACTION_CONDITION_TYPE]
